/**
 * Hardware Products hook using React Relay
 *
 * Fetches hardware products for dropdowns and filters,
 * optionally filtered by hardware type.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useLazyLoadQuery, fetchQuery } from 'react-relay';
import { useState, useEffect, useCallback, useMemo } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useHardwareProductsRelayQuery as UseHardwareProductsRelayQuery,
  HardwareProductFilterInput,
} from './__generated__/useHardwareProductsRelayQuery.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Query for hardware products list with optional type filter
 */
const HardwareProductsQuery = graphql`
  query useHardwareProductsRelayQuery($first: Int, $filter: HardwareProductFilterInput) {
    listHardwareProducts(
      first: $first
      filter: $filter
      sort: [{ field: NAME, order: ASC }]
    ) {
      edges {
        node {
          id
          oldId
          name
          description
          vendor
          hasMac
          hasImei
          hasBatteryStatus
          hasLocationTracking
          hasNumber
          hasTcpConnection
          hasVendorId
          hasVendorSerial
          hasBle
          primaryIdentifier
          requireLabel
          supportsRemoteBatteryLock
          supportsRemoteBatteryStatus
          supportsRemoteBatteryUnlock
          supportsRemoteLocationCheck
          supportsRemoteRing
          supportsRemoteUnlock
          vendorSerialDescription
          vendorSerialRegex
          vendorSerialValidationRegex
          hardwareTypeId
          hardwareType {
            id
            name
          }
        }
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { HardwareProductFilterInput };

/**
 * Hardware Product from GraphQL
 */
export interface HardwareProductGraphQL {
  id: string;
  oldId?: string | null;
  name?: string | null;
  description?: string | null;
  vendor?: string | null;
  hasMac?: boolean | null;
  hasImei?: boolean | null;
  hasBatteryStatus?: boolean | null;
  hasLocationTracking?: boolean | null;
  hasNumber?: boolean | null;
  hasTcpConnection?: boolean | null;
  hasVendorId?: boolean | null;
  hasVendorSerial?: boolean | null;
  hasBle?: boolean | null;
  primaryIdentifier?: string | null;
  requireLabel?: boolean | null;
  supportsRemoteBatteryLock?: boolean | null;
  supportsRemoteBatteryStatus?: boolean | null;
  supportsRemoteBatteryUnlock?: boolean | null;
  supportsRemoteLocationCheck?: boolean | null;
  supportsRemoteRing?: boolean | null;
  supportsRemoteUnlock?: boolean | null;
  vendorSerialDescription?: string | null;
  vendorSerialRegex?: string | null;
  vendorSerialValidationRegex?: string | null;
  hardwareTypeId?: string | null;
  hardwareType?: {
    id: string;
    name?: string | null;
  } | null;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching hardware products using Relay with Suspense
 *
 * Uses Suspense - wrap with Suspense boundary
 *
 * @example
 * function HardwareProductSelect({ hardwareTypeId }) {
 *   return (
 *     <Suspense fallback={<Loading />}>
 *       <HardwareProductSelectContent hardwareTypeId={hardwareTypeId} />
 *     </Suspense>
 *   );
 * }
 *
 * function HardwareProductSelectContent({ hardwareTypeId }) {
 *   const products = useHardwareProductsRelay({ hardwareTypeId });
 *   return <Select options={products.map(p => ({ value: p.id, label: p.name }))} />;
 * }
 */
export function useHardwareProductsRelay(options?: {
  hardwareTypeId?: string;
  limit?: number;
}) {
  const filter: HardwareProductFilterInput | undefined = options?.hardwareTypeId
    ? { hardwareTypeId: { eq: options.hardwareTypeId } }
    : undefined;

  const data = useLazyLoadQuery<UseHardwareProductsRelayQuery>(HardwareProductsQuery, {
    first: options?.limit ?? 100,
    filter,
  });

  const products =
    data.listHardwareProducts?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return products as HardwareProductGraphQL[];
}

// ============================================================================
// Non-Suspense Hook (with loading state)
// ============================================================================

/**
 * Hook that provides React Query-like interface for hardware products
 *
 * This hook does NOT use Suspense - it provides isLoading, data, refetch etc.
 *
 * @example
 * const { data, isLoading } = useHardwareProductsRelayQuery({ hardwareTypeId });
 *
 * if (isLoading) return <Loading />;
 * return <Select options={data.map(p => ({ value: p.id, label: p.name }))} />;
 */
export function useHardwareProductsRelayQuery(options?: {
  enabled?: boolean;
  hardwareTypeId?: string;
  limit?: number;
}) {
  const [data, setData] = useState<HardwareProductGraphQL[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false;
  const limit = options?.limit ?? 100;
  const hardwareTypeId = options?.hardwareTypeId;

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        setIsLoading(true);

        const filter: HardwareProductFilterInput | undefined = hardwareTypeId
          ? { hardwareTypeId: { eq: hardwareTypeId } }
          : undefined;

      const result = await fetchQuery<UseHardwareProductsRelayQuery>(
          relayEnvironment,
          HardwareProductsQuery,
          { first: limit, filter }
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const products =
            result.listHardwareProducts?.edges
              ?.map((edge) => edge?.node)
              .filter(Boolean) ?? [];
          setData(products as HardwareProductGraphQL[]);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err : new Error('Failed to fetch hardware products')
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
  }, [enabled, limit, hardwareTypeId, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return useMemo(
    () => ({
      data,
      isLoading,
      isFetched,
      error,
      refetch,
    }),
    [data, isLoading, isFetched, error, refetch]
  );
}
